'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { aclService } from '@/lib/api/aclService';
import { ACL, ACLUpdate } from '@/types/acl';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Toast, ToastType } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

// Iconos
const SaveIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Estructura de campos organizados por categoría
interface FieldCategory {
  id: string;
  name: string;
  description?: string;
  fields: string[];
}

const FIELD_CATEGORIES: FieldCategory[] = [
  {
    id: 'menus-solicitudes',
    name: 'Menús de Solicitudes y Proceso',
    fields: [
      'menuSolicitudes',
      'menuContactados',
      'menuEntregadosaRiesgos',
      'menuMesaDeAnalisis',
      'menuRevisionDocumental',
      'menuPreAnalisis',
      'menuAnalisisCreditos',
      'menuSolicitarVoBos',
      'menuAutorizacionRiesgos',
      'menuAutorizacionDG',
      'menuImprimirContrato',
      'menuFirmarContrato',
      'menuPorFondear',
      'menuFondeados',
      'menuRechazados',
    ],
  },
  {
    id: 'menus-operaciones',
    name: 'Menús de Operaciones',
    fields: [
      'menuTransferencias',
      'menuControlExpedientes',
      'menuCobranza',
      'menuClabes',
      'menuHistorico',
      'menuAgendas',
      'menuVisitas',
    ],
  },
  {
    id: 'menus-reportes',
    name: 'Menús de Reportes y Análisis',
    fields: [
      'menuPipeLine',
      'menuScoreCard',
      'menuColocacion',
      'menuCumplimiento',
      'menuCosechas',
      'menuOIP',
      'menuReporteVentas',
      'menuReporteTexto',
      'seguimientoDiario',
    ],
  },
  {
    id: 'menus-administracion',
    name: 'Menús de Administración',
    fields: [
      'menuUsuarios',
      'menuACL',
      'menuEmpresas',
      'menuPagadoras',
      'menuAsignaciones',
      'menuLogs',
      'menuBackUps',
      'menuSesiones',
      'menuPerfil',
      'menuSimulador',
      'menuAyuda',
      'accionConfigurarCorreo',
    ],
  },
  {
    id: 'solicitudes-asesor',
    name: 'Campos de Solicitudes - Asesor y Región',
    fields: [
      'asesorID',
      'inheritedID',
      'asesorNombre',
      'ownerID',
      'regionNombre',
    ],
  },
  {
    id: 'solicitudes-empresa',
    name: 'Campos de Solicitudes - Empresa',
    fields: [
      'clienteEmpresa',
      'voboRequerido',
      'clienteTipoEmpresa',
      'clienteNivelRiesgoEmpresa',
      'clientePagadora',
    ],
  },
  {
    id: 'solicitudes-cliente',
    name: 'Campos de Solicitudes - Cliente',
    fields: [
      'clienteNombre',
      'clienteApellidoPaterno',
      'clienteApellidoMaterno',
      'clienteNuevoRenovacion',
      'clientePuesto',
      'clienteDirectivo',
      'clienteSindicalizado',
      'clienteAntiguedad',
      'clienteFechaIngreso',
      'clienteAntiguedadMeses',
      'clienteClabe',
      'clienteCorreo',
      'clienteNombreJefe',
      'clienteCorreoJefe',
      'clienteSeEnteroPor',
    ],
  },
  {
    id: 'solicitudes-pago',
    name: 'Campos de Solicitudes - Pago y Salario',
    fields: [
      'frecuenciaDePago',
      'clienteSalario',
      'clienteDeduccionesSemanales',
      'clienteDeduccionesQuincenales',
      'clienteDeduccionesMensuales',
      'clienteDeduccionesPeriodo',
    ],
  },
  {
    id: 'solicitudes-producto',
    name: 'Campos de Solicitudes - Producto y Solicitud',
    fields: [
      'producto',
      'montoSolicitado',
      'solicitudDestinoCredito',
      'plazoSolicitado',
      'polizaSeguro',
      'solicitudEsCleanUp',
      'solicitudNumeroControl',
      'solicitudNumeroControlAnterior',
      'solicitudDeCredito',
      'solcitudDeSeguro',
    ],
  },
  {
    id: 'solicitudes-historial',
    name: 'Campos de Solicitudes - Historial y Créditos Anteriores',
    fields: [
      'solicitudTasaTipoAnterior',
      'solicitudSaldoActualSIACAnterior',
      'solicitudTotalPagadoSIACAnterior',
      'solicitudSaldoParcialidadesNumeroAnterior',
      'solicitudSaldoParcialidadesMontoAnterior',
      'solicitudMontoOriginalAnterior',
      'solicitudAvanceCreditoAnterior',
      'solicitudIncrementoSaldoActual',
      'solicitudSaldoTotalAnterior',
    ],
  },
  {
    id: 'solicitudes-analisis',
    name: 'Campos de Solicitudes - Análisis y Evaluación',
    fields: [
      'analistaNombre',
      'estatusAnalisis',
      'buenBuroMontoOferta',
      'buenBuroEstatus',
      'buenBuroMontoAceptado',
      'tipoBuroCredito',
      'montoBuenBuro',
      'tipoListaNegra',
      'saldo1a89',
      'saldoMayor90',
      'saldoMayor120',
      'solicitudMop',
    ],
  },
  {
    id: 'solicitudes-politicas',
    name: 'Campos de Solicitudes - Políticas y Límites',
    fields: [
      'montoMaximoPolitica',
      'montoPropuesto',
      'plazoMaximoPolitica',
      'tasaDeComisionPolitica',
      'montoComisionPolitica',
      'tasaPolitica',
      'clientePrestarHasta',
      'clienteAvalRequerido',
      'lineaDeCredito',
      'fechaVencimientoLineaDeCredito',
    ],
  },
  {
    id: 'solicitudes-autorizacion',
    name: 'Campos de Solicitudes - Autorización',
    fields: [
      'montoAutorizado',
      'plazoAutorizado',
      'tasaAutorizada',
      'tasaDeComision',
      'montoComision',
      'autorizacionDG',
      'usuarioAutorizacionRiesgos',
    ],
  },
  {
    id: 'solicitudes-transferencias',
    name: 'Campos de Solicitudes - Transferencias',
    fields: [
      'montoTransferencia',
      'errorTransferencia',
    ],
  },
  {
    id: 'solicitudes-documentos',
    name: 'Campos de Solicitudes - Documentos',
    fields: [
      'identificacionOficial',
      'validacionINE',
      'formatoCURP',
      'comprobanteDomicilio',
      'validacionSueldos',
      'estadoDeCuenta',
      'listasNegras',
      'solicitudBuroCredito',
      'accionGuardarDocumentos',
      'documentos',
      'addArchivos',
      'listaArchivos',
    ],
  },
  {
    id: 'solicitudes-fechas',
    name: 'Campos de Solicitudes - Fechas del Proceso',
    fields: [
      'fechaContacto',
      'fechaEntregaARiesgos',
      'fechaValidacionClabe',
      'fechaRevisionDocumentalConluida',
      'fechaAnalisisConluido',
      'fechaPrimerSolicitudVoBo',
      'fechaSegundaSolicitudVoBo',
      'fechaTercerSolicitudVoBo',
      'fechaVoBo',
      'fechaPropuesta',
      'fechaRechazoRiesgos',
      'solicitudCausaRechazoRiesgos',
      'fechaAutorizacionDG',
      'fechaRechazoDG',
      'fechaEmisionContrato',
      'fechaContratoImpreso',
      'fechaEntregaContratoFirmado',
      'fechaPrimerCobro',
      'fechaFondeado',
      'fechaCancelacionCliente',
      'fechaCancelacionCartera',
      'fechaReferenciaCobranza',
    ],
  },
  {
    id: 'solicitudes-expedientes',
    name: 'Campos de Solicitudes - Expedientes',
    fields: [
      'fechaExpedienteRecibidoRiesgos',
      'fechaExpedienteRevisadoRiesgos',
      'fechaExpedienteDevueltoAComercial',
      'fechaExpedienteResguardadoEnBoveda',
      'usuarioExpedienteRecibidoRiesgos',
      'usuarioExpedienteRevisadoRiesgos',
      'usuarioExpedienteDevueltoAComercial',
      'usuarioExpedienteResguardadoEnBoveda',
      'expedienteResguardoStatus',
      'comentariosResguardoExpedientes',
    ],
  },
  {
    id: 'solicitudes-cobranza',
    name: 'Campos de Solicitudes - Cobranza',
    fields: [
      'clienteReferenciaCobranza',
    ],
  },
  {
    id: 'solicitudes-calculos',
    name: 'Campos de Solicitudes - Cálculos y Capacidad de Pago',
    fields: [
      'numeroPagos',
      'montoCapacidadDePago',
      'pagoEstimadoPeriodo',
      'costoSeguroParcialidad',
      'pagoTotalPeriodo',
      'capacidadDePago',
    ],
  },
  {
    id: 'solicitudes-excepciones',
    name: 'Campos de Solicitudes - Excepciones',
    fields: [
      'excepcionDocumentada1',
      'excepcionDocumentada2',
      'excepcionDocumentada3',
    ],
  },
  {
    id: 'solicitudes-generales',
    name: 'Campos de Solicitudes - Generales',
    fields: [
      'solicitudEstatus',
      'solicitudSeguroFinanciado',
      'contratoDocumento',
      'comentarios',
      'accionRealizarAnalisis',
      'id',
    ],
  },
  {
    id: 'usuarios',
    name: 'Campos de Usuarios',
    fields: [
      'ownerID',
      'name',
      'userName',
      'correo',
      'password',
      'puesto',
      'region',
      'userEstatus',
      'tipoUsuario',
      'acl',
    ],
  },
  {
    id: 'empresas',
    name: 'Campos de Empresas',
    fields: [
      'id',
      'nombre',
      'agrupacion',
      'empresaPagadora',
      'empresaSegmentacionCobranza',
      'tipo',
      'region',
      'autorizacionDG',
      'voboRequerido',
      'estatus',
      'nivel',
    ],
  },
  {
    id: 'roip',
    name: 'Campos de ROIP (Operaciones Internas Preocupantes)',
    fields: [
      'solicitudNumeroControl',
      'clienteNombre',
      'userName',
      'roi',
      'reportadoPor',
      'reporteEstatus',
      'fechaReporte',
      'fechaDictaminacion',
      'fechaReporteSITI',
      'documentosROIP',
      'FECHA PROSPECTO',
      'AÑO CREDITO',
      'PROMOTOR',
      'EMPRESA CONVENIO',
      'FUENTE',
      'TIPO DE PRODUCTO',
      'ETAPA',
      'ESTATUS',
      'FECHA COTIZACIÓN',
    ],
  },
  {
    id: 'control-especial',
    name: 'Campos Especiales de Control',
    fields: [
      'menuACL',
      'acl',
    ],
  },
];

// Lista plana de todos los campos para el resumen
const getAllFields = (): string[] => {
  return FIELD_CATEGORIES.flatMap(category => category.fields);
};

const FORBIDDEN_OPTIONS = getAllFields();

const SCOPE_OPTIONS = [
  'all', // Ver todo
  'self', // Solo lo propio/asignado
];

const ACLManagement: React.FC = () => {
  const [acls, setAcls] = useState<ACL[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [localChanges, setLocalChanges] = useState<Record<string, ACLUpdate>>({});
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newRoleName, setNewRoleName] = useState<string>('');
  const [newRoleScope, setNewRoleScope] = useState<string>('all');
  const [deleting, setDeleting] = useState<boolean>(false);
  const [toast, setToast] = useState<{ isOpen: boolean; message: string; type: ToastType }>({
    isOpen: false,
    message: '',
    type: 'info',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
  });
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(FIELD_CATEGORIES.map(cat => cat.id))
  );

  // Helper para mostrar toast
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ isOpen: true, message, type });
  };

  // Toggle de categoría expandida/colapsada
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Toggle de todos los elementos de una categoría
  const toggleCategoryAll = (categoryFields: string[]) => {
    if (!selectedRole || !currentACL) return;

    const currentForbidden = currentACL.forbidden || [];
    
    // Verificar si todos los campos de la categoría están permitidos
    const allAllowed = categoryFields.every(item => !currentForbidden.includes(item));
    
    let newForbidden: string[];
    if (allAllowed) {
      // Si todos están permitidos, prohibirlos todos
      newForbidden = [...currentForbidden, ...categoryFields];
    } else {
      // Si alguno está prohibido, permitirlos todos (remover de forbidden)
      newForbidden = currentForbidden.filter(item => !categoryFields.includes(item));
    }

    setLocalChanges(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        forbidden: newForbidden,
      },
    }));
  };

  // Cargar ACLs
  const fetchACLs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await aclService.getACLs();
      if (response.data) {
        // Asegurar que forbidden siempre sea un array
        const normalizedAcls = response.data.map(acl => ({
          ...acl,
          forbidden: Array.isArray(acl.forbidden) ? acl.forbidden : (Array.isArray(acl.forbidden_fields) ? acl.forbidden_fields : []),
        }));
        console.log('ACLs cargados:', normalizedAcls);
        console.log('Ejemplo sysAdmin:', normalizedAcls.find(a => a.acl_name === 'sysAdmin'));
        console.log('Ejemplo gerenteRiesgos:', normalizedAcls.find(a => a.acl_name === 'gerenteRiesgos'));
        setAcls(normalizedAcls);
        setLocalChanges({});
        // Si hay ACLs y no hay rol seleccionado, seleccionar el primero
        if (normalizedAcls.length > 0 && !selectedRole) {
          setSelectedRole(normalizedAcls[0].acl_name);
        }
      } else {
        const errorMsg = response.error || 'Error al cargar ACLs';
        setError(errorMsg);
        console.error('Error al cargar ACLs:', response);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error de conexión al obtener ACLs';
      setError(errorMsg);
      console.error('Excepción al cargar ACLs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchACLs();
  }, []);

  // Filtrar ACLs por búsqueda
  const filteredAcls = useMemo(() => {
    if (!searchTerm) return acls;
    const term = searchTerm.toLowerCase();
    return acls.filter(acl => 
      acl.acl_name.toLowerCase().includes(term) ||
      acl.scope.toLowerCase().includes(term)
    );
  }, [acls, searchTerm]);

  // Obtener ACL seleccionado
  const selectedACL = useMemo(() => {
    if (!selectedRole) return null;
    return acls.find(acl => acl.acl_name === selectedRole) || null;
  }, [acls, selectedRole]);

  // Obtener cambios locales para el ACL seleccionado
  const currentChanges = useMemo(() => {
    if (!selectedRole) return null;
    return localChanges[selectedRole] || null;
  }, [localChanges, selectedRole]);

  // ACL actual (con cambios locales aplicados)
  const currentACL = useMemo(() => {
    if (!selectedACL) return null;
    const changes = currentChanges;
    if (!changes) return selectedACL;
    
    return {
      ...selectedACL,
      scope: changes.scope !== undefined ? changes.scope : selectedACL.scope,
      forbidden: changes.forbidden !== undefined ? changes.forbidden : selectedACL.forbidden,
    };
  }, [selectedACL, currentChanges]);

  // Toggle de elemento permitido/prohibido
  // Lógica invertida: switch ON = permitido, switch OFF = prohibido
  const toggleForbidden = (item: string) => {
    if (!selectedRole || !currentACL) return;

    const currentForbidden = currentACL.forbidden || [];
    // Si el item está en forbidden (prohibido), lo quitamos (permitir)
    // Si el item NO está en forbidden (permitido), lo agregamos (prohibir)
    const newForbidden = currentForbidden.includes(item)
      ? currentForbidden.filter(f => f !== item)
      : [...currentForbidden, item];

    setLocalChanges(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        forbidden: newForbidden,
      },
    }));
  };

  // Cambiar scope
  const changeScope = (scope: string) => {
    if (!selectedRole) return;
    
    setLocalChanges(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        scope,
      },
    }));
  };

  // Guardar cambios
  const saveChanges = async () => {
    if (!selectedRole || !currentChanges) return;

    setSaving(true);
    try {
      const response = await aclService.updateACL(selectedRole, currentChanges);
      if (response.data) {
        await fetchACLs();
        setLocalChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[selectedRole];
          return newChanges;
        });
        showToast('Cambios guardados exitosamente', 'success');
      } else {
        showToast(response.error || 'Error al guardar cambios', 'error');
      }
    } catch (err) {
      showToast('Error de conexión al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Verificar si hay cambios sin guardar
  const hasUnsavedChanges = Object.keys(localChanges).length > 0;

  // Crear nuevo rol
  const createRole = async () => {
    if (!newRoleName.trim()) {
      showToast('Por favor ingresa un nombre para el rol', 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await aclService.createACL({
        acl_name: newRoleName.trim(),
        scope: newRoleScope,
        forbidden: []
      });

      if (response.data) {
        await fetchACLs();
        setShowCreateModal(false);
        setNewRoleName('');
        setNewRoleScope('all');
        setSelectedRole(newRoleName.trim());
        showToast('Rol creado exitosamente', 'success');
      } else {
        showToast(response.error || 'Error al crear el rol', 'error');
      }
    } catch (err) {
      showToast('Error de conexión al crear el rol', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar rol
  const handleDeleteClick = () => {
    if (!selectedRole) return;

    const criticalRoles = ['sysAdmin', 'all', 'all_limited'];
    if (criticalRoles.includes(selectedRole)) {
      showToast(`No se puede eliminar el rol '${selectedRole}' porque es crítico para el sistema`, 'warning');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Confirmar eliminación',
      message: `¿Estás seguro de que deseas eliminar el rol '${selectedRole}'? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        setDeleting(true);
        try {
          const response = await aclService.deleteACL(selectedRole);
          if (response.data) {
            await fetchACLs();
            setSelectedRole(null);
            showToast('Rol eliminado exitosamente', 'success');
          } else {
            showToast(response.error || 'Error al eliminar el rol', 'error');
          }
        } catch (err) {
          showToast('Error de conexión al eliminar el rol', 'error');
        } finally {
          setDeleting(false);
        }
      },
      variant: 'danger',
    });
  };

  if (loading && acls.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        <p className="text-sm text-gray-500">Cargando configuración de ACL...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Control de Acceso (ACL)</h1>
            <p className="text-sm text-gray-500 mt-1">Gestiona permisos y restricciones por rol</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            onClick={fetchACLs}
            disabled={loading}
          >
            <RefreshIcon />
            Actualizar
          </Button>
          <Button 
            variant="ghost"
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
            className="bg-green-50 hover:bg-green-100 text-green-700 border border-green-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Rol
          </Button>
          {hasUnsavedChanges && (
            <Button 
              onClick={saveChanges}
              disabled={saving || !selectedRole}
            >
              <SaveIcon />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      {!loading && acls.length === 0 && !error && (
        <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-semibold text-yellow-900">No hay ACLs configurados</p>
              <p className="text-sm text-yellow-700 mt-1">
                No se encontraron configuraciones de ACL en la base de datos. 
                Contacta al administrador del sistema para crear los roles necesarios.
              </p>
            </div>
          </div>
        </div>
      )}

      {acls.length > 0 && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel izquierdo: Lista de roles */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Buscar rol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filteredAcls.map((acl) => (
              <button
                key={acl.id}
                onClick={() => setSelectedRole(acl.acl_name)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedRole === acl.acl_name
                    ? 'bg-blue-50 border-2 border-blue-500 text-blue-900'
                    : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{acl.acl_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Scope: {acl.scope}
                    </div>
                  </div>
                  {localChanges[acl.acl_name] && (
                    <Badge variant="warning">
                      Sin guardar
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Panel derecho: Configuración del rol seleccionado */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {!selectedRole || !currentACL ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <ShieldIcon />
              <p className="mt-4 text-lg">Selecciona un rol para configurar</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header del rol */}
              <div className="border-b border-gray-200 pb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{currentACL.acl_name}</h2>
                  <p className="text-sm text-gray-500 mt-1">Configura los permisos para este rol</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleDeleteClick}
                  disabled={deleting}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Alcance (Scope)
                </label>
                <div className="space-y-3">
                  <div className="text-xs text-gray-500 mb-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="font-semibold text-gray-700 mb-2">Cómo funciona el scope:</div>
                    <div className="space-y-1">
                      <div><strong>all:</strong> Ve todas las solicitudes/agendas. En formularios puede seleccionar cualquier asesor.</div>
                      <div><strong>self:</strong> Solo ve sus propias solicitudes/agendas (asignadas a él). En formularios el asesor está bloqueado a su nombre.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {SCOPE_OPTIONS.map((scope) => (
                      <button
                        key={scope}
                        onClick={() => changeScope(scope)}
                        className={`p-4 rounded-lg border-2 transition-colors text-sm font-medium ${
                          currentACL.scope === scope
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                        }`}
                      >
                        <div className="font-bold text-base mb-1 uppercase">{scope}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {scope === 'all' 
                            ? 'Acceso completo a todos los registros'
                            : 'Solo registros propios/asignados'
                          }
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Elementos Permitidos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    Elementos Permitidos
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExpandedCategories(new Set(FIELD_CATEGORIES.map(cat => cat.id)))}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Expandir Todo
                    </button>
                    <span className="text-xs text-gray-400">|</span>
                    <button
                      onClick={() => setExpandedCategories(new Set())}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Colapsar Todo
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Activa los switches para permitir acceso a menús, campos o rutas. Organizados por categoría.
                </p>
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {FIELD_CATEGORIES.map((category) => {
                    const isExpanded = expandedCategories.has(category.id);
                    const categoryFields = category.fields;
                    const allowedInCategory = categoryFields.filter(
                      item => !(currentACL.forbidden?.includes(item) || false)
                    ).length;
                    const totalInCategory = categoryFields.length;

                    return (
                      <div
                        key={category.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Header de categoría */}
                        <div className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <button
                            onClick={() => toggleCategory(category.id)}
                            className="flex items-center gap-2 flex-1"
                          >
                            <svg
                              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-sm font-semibold text-gray-700">{category.name}</span>
                            <span className="text-xs text-gray-500">
                              ({allowedInCategory}/{totalInCategory} permitidos)
                            </span>
                          </button>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">Todo:</span>
                              <Switch
                                checked={allowedInCategory === totalInCategory}
                                onChange={() => toggleCategoryAll(categoryFields)}
                                color={allowedInCategory === totalInCategory ? 'green' : 'red'}
                                disabled={!selectedRole}
                              />
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              allowedInCategory === totalInCategory
                                ? 'bg-green-100 text-green-700'
                                : allowedInCategory === 0
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {allowedInCategory === totalInCategory ? 'Todo permitido' : 
                               allowedInCategory === 0 ? 'Todo prohibido' : 'Parcial'}
                            </span>
                          </div>
                        </div>

                        {/* Campos de la categoría */}
                        {isExpanded && (
                          <div className="bg-white p-2 pl-6 space-y-2 border-l-2 border-gray-200 ml-2">
                            {categoryFields.map((item) => {
                              // Lógica invertida: si NO está en forbidden, está permitido
                              const isAllowed = !(currentACL.forbidden?.includes(item) || false);
                              return (
                                <div
                                  key={item}
                                  className="flex items-center justify-between p-2 rounded-lg transition-colors bg-gray-50 border border-gray-100 hover:bg-gray-100"
                                >
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {item}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {item.startsWith('menu') && 'Menú'}
                                      {item.startsWith('/') && 'Ruta'}
                                      {!item.startsWith('menu') && !item.startsWith('/') && 'Campo'}
                                      {' • '}
                                      {isAllowed ? (
                                        <span className="font-semibold text-green-600">Permitido</span>
                                      ) : (
                                        <span className="font-semibold text-red-600">Prohibido</span>
                                      )}
                                    </div>
                                  </div>
                                  <Switch
                                    checked={isAllowed}
                                    onChange={() => toggleForbidden(item)}
                                    label={isAllowed ? 'Permitido' : 'Prohibido'}
                                    color={isAllowed ? 'green' : 'red'}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Resumen */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-blue-900 mb-2">Resumen</div>
                <div className="text-xs text-blue-700 space-y-1">
                  <div>Scope: <strong>{currentACL.scope}</strong></div>
                  <div>
                    Elementos permitidos: <strong className="text-green-700">{FORBIDDEN_OPTIONS.length - (currentACL.forbidden?.length || 0)}</strong> / {FORBIDDEN_OPTIONS.length}
                  </div>
                  <div>
                    Elementos prohibidos: <strong className="text-red-700">{currentACL.forbidden?.length || 0}</strong>
                  </div>
                  <div>
                    Categorías: <strong>{FIELD_CATEGORIES.length}</strong>
                  </div>
                  {hasUnsavedChanges && (
                    <div className="mt-2 text-orange-600 font-semibold">
                      ⚠️ Tienes cambios sin guardar
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Modal para crear nuevo rol */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewRoleName('');
          setNewRoleScope('all');
        }}
        title="Crear Nuevo Rol"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nombre del Rol
            </label>
            <Input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="Ej: nuevoRol"
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Usa camelCase (ej: gerenteVentas, analistaContable)
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Scope Inicial
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SCOPE_OPTIONS.map((scope) => (
                <button
                  key={scope}
                  onClick={() => setNewRoleScope(scope)}
                  className={`p-3 rounded-lg border-2 transition-colors text-sm font-medium ${
                    newRoleScope === scope
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreateModal(false);
              setNewRoleName('');
              setNewRoleScope('all');
            }}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={createRole}
            disabled={saving || !newRoleName.trim()}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? 'Creando...' : 'Crear Rol'}
          </Button>
        </div>
      </Modal>

      {/* Toast para notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      {/* Dialog de confirmación */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        loading={deleting}
      />
    </div>
  );
};

export default ACLManagement;
